import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Typography, Popover, MenuItem, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useFeathers } from '../../app/util';

const coveragePlanOptions = {
  race: [
    { value: 'auto-coverage', label: 'Auto coverage', description: 'Full issues information, where first draft is created by our algorithm.' },
    { value: 'basic-coverage', label: 'Basic info', description: 'Only basic information, such as website, photo, and social media.' },
    { value: 'no-coverage', label: 'No coverage', description: 'Only candidate name' }
  ],
  measure: [
    { value: 'auto-coverage', label: 'Auto coverage', description: 'Full measure coverage will be provided by default.' },
    { value: 'no-coverage', label: 'No coverage', description: 'No narrative coverage will be provided for this measure.' }
  ]
};

const CoveragePlanSelect = ({ manualValue, onChangeCoveragePlan, style, district, office, election, contentType = 'race' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultCoveragePlan, setDefaultCoveragePlan] = useState(null);
  const feathers = useFeathers();

  useEffect(() => {
    const fetchCoveragePlan = async () => {
      const allFields = [
        district,
        ...(contentType === 'race' ? [office] : []),
        election
      ]

      console.log('allFields', allFields)
      console.log('manualValue', manualValue)

      // Only fetch if we have all required fields and no value is set yet
      if (allFields.every(field => field) && !manualValue) {
        setIsLoading(true);
        try {
          const result = await feathers.getService('coverage-plans').create({
            district: district._id ?? district,
            ...(contentType === 'race' ? { office: office.key ?? office } : {}),
            election: election.key ?? election,
            contentType
          });
          
          if (result?.coveragePlan) {
            setDefaultCoveragePlan(result.coveragePlan);
          }
        } catch (error) {
          console.error('Error fetching coverage plan:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCoveragePlan();
  }, [district, office, election, manualValue, onChangeCoveragePlan, feathers, contentType]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue) => {
    onChangeCoveragePlan(newValue);
    handleClose();
  };

  const options = coveragePlanOptions[contentType] || coveragePlanOptions.race;
  const selectedOption = options.find(opt => opt.value === (manualValue ?? defaultCoveragePlan));

  return (
    <Wrapper 
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TextContainer onClick={handleClick}>
        <Typography variant="body1">
          {selectedOption?.label || 'Select coverage plan'}
        </Typography>
        {isLoading ? (
          <LoadingSpinner size={16} />
        ) : isHovered && (
          <EditIconStyled />
        )}
      </TextContainer>
      
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuContainer>
          {options.map((option) => (
            <MenuItem 
              key={option.value} 
              value={option.value}
              onClick={() => handleSelect(option.value)}
              selected={(manualValue ?? defaultCoveragePlan) === option.value}
            >
              <MenuItemContent>
                <Typography variant="body1">{option.label}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {option.description}
                </Typography>
              </MenuItemContent>
            </MenuItem>
          ))}
        </MenuContainer>
      </Popover>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  flex-grow: 1;
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const EditIconStyled = styled(EditIcon)`
  font-size: 16px;
  opacity: 0.7;
`;

const MenuContainer = styled.div`
  min-width: 300px;
  max-width: 400px;
`;

const MenuItemContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
`;

const LoadingSpinner = styled(CircularProgress)`
  margin-left: 8px;
`;

export { CoveragePlanSelect, coveragePlanOptions }; 